/**
 * 🔥 QuickHeal — 2-second emergency heal panel on /pet.
 *
 * Shows the 3 HP consumables (Phoenix Flint / Aglare Quill / Ignis Core)
 * with their owned count + heal value. Single tap consumes one and
 * restores HP. Buttons auto-disable when:
 *   - owned count is 0
 *   - pet is at max HP
 */
import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RARITY_COLOR = {
  common:    "border-neutral-700",
  uncommon:  "border-emerald-700",
  rare:      "border-cyan-700",
  epic:      "border-purple-700",
  legendary: "border-amber-700",
};

export default function QuickHeal({ pet, hpItems, onHealed }) {
  const [busy, setBusy] = useState(null);
  const max = Number(pet?.max_hp || 0);
  const cur = Number(pet?.current_hp ?? max);
  const atMax = cur >= max;
  // Ticket 4 fix: clamp pct 0..100.
  const pct = max ? Math.min(100, Math.max(0, Math.round((cur / max) * 100))) : 0;

  const onUse = useCallback(async (itemId) => {
    if (busy) return;
    setBusy(itemId);
    try {
      const r = await axios.post(`${API}/pet/use-hp-item`, { item_id: itemId });
      toast.success(`+${r.data.healed} HP — now ${r.data.current_hp}/${r.data.max_hp}`);
      onHealed?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't use item.");
    } finally {
      setBusy(null);
    }
  }, [busy, onHealed]);

  const items = hpItems || {};
  const order = ["phoenix_flint", "aglare_quill", "ignis_core"];

  return (
    <div className="mb-6 bg-[#0F0F0F] border-2 border-neutral-800 p-4" data-testid="quick-heal">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#FCD34D] font-bold">🔥 Quick-Heal</div>
          <div className="font-display text-base">
            HP <span className="font-mono tabular-nums text-sm">{cur} / {max}</span>
          </div>
        </div>
        {atMax && (
          <span className="text-[9px] uppercase tracking-widest font-bold text-emerald-400 border border-emerald-900 px-2 py-1">
            ✓ Full HP
          </span>
        )}
      </div>
      {/* HP bar */}
      <div className="h-2 bg-black border border-zinc-900 overflow-hidden mb-3">
        <div
          className={`h-full transition-all ${
            pct > 60 ? "bg-gradient-to-r from-emerald-700 to-emerald-500" :
            pct > 30 ? "bg-gradient-to-r from-amber-700 to-amber-500" :
                       "bg-gradient-to-r from-red-700 to-red-500"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {order.map((id) => {
          const it = items[id];
          if (!it) return null;
          const owned = Number(it.owned || 0);
          const disabled = owned === 0 || atMax || busy === id;
          return (
            <button
              key={id}
              onClick={() => onUse(id)}
              disabled={disabled}
              data-testid={`quick-heal-${id}`}
              title={it.tagline}
              className={`flex flex-col items-center p-2 border-2 transition relative ${
                disabled
                  ? "border-zinc-900 bg-neutral-950 opacity-50 cursor-not-allowed"
                  : `${RARITY_COLOR[it.rarity] || "border-neutral-700"} bg-neutral-950 hover:bg-neutral-900 hover:border-[#D4AF37]`
              }`}
            >
              <div className="text-2xl leading-none mb-1">{it.icon}</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-center leading-tight">
                {it.name}
              </div>
              <div className="text-[9px] text-[#FCD34D] font-mono mt-0.5">+{it.heal} HP</div>
              <span
                className={`absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 text-[10px] font-mono font-bold flex items-center justify-center ${
                  owned > 0 ? "bg-[#D4AF37] text-black" : "bg-zinc-900 text-neutral-600"
                }`}
                data-testid={`quick-heal-count-${id}`}
              >
                {owned}
              </span>
            </button>
          );
        })}
      </div>
      <div className="text-[10px] text-neutral-600 mt-2 leading-snug">
        💡 Find these in 🌲 The Wilds or craft in 🍳 Dave's Kitchen (coming soon). Soulbound — not tradeable.
      </div>
    </div>
  );
}
